import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TAvatarMenuOptions } from '@celebration/design-system/dist/lib/avatar/props.types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LOGO from '../../assets/png/logo.png';
import ProfileIcon from '../../assets/svg/ambev-user.svg';
import Auth from '../../../app/contexts/auth';
import CarriersConstants from '../../../pages/carriers/constants';
import './header.scss';
import { SULAHUB_ROLES } from '../../constants/roles';
import UsersConstants from '../../../pages/users/constants';

import CustomHeader from '../custom-header';
import HomeConstants from '../../../pages/home/constants';
import LoadsConstants from '../../../pages/business/loads/constants';

import ReportsConstants from '../../../pages/reports/constants';
import PreferencesConstants from '../../../pages/preferences/constants';
import { featureType } from '../feature-flag/interfaces/IFeatureFlagProps';
import isFeatureFlagEnabled from '../feature-flag/feature-flag.service';
import RoundsConstants from '../../../pages/smartcargas/rounds/constants';
import WonOfferConstants from '../../../pages/smartcargas/won-offers/constants';
import { NotificationsProvider } from '../../../app/contexts/notifications/notifications.context';
import PoolConstants from '../../../pages/pool/constants';

function HeaderComponent() {
    const {
        t,
        i18n: { changeLanguage },
    } = useTranslation();
    const { user, signOut, isAuthorized } = Auth.useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const userName = user ? user.name : '';

    const [gerencialFeatureFlag, setGerencialFeatureFlag] =
        useState<boolean>(false);

    const [smartWebFeatureFlag, setSmartWebFeatureFlag] =
        useState<boolean>(false);

    const [smartPoolFeatureFlag, setSmartPoolFeatureFlag] =
        useState<boolean>(false);

    const homeButton = useMemo(() => {
        return {
            id: 0,
            opened: pathname.startsWith(HomeConstants.PATH),
            path: HomeConstants.PATH,
            isLink: true,
            label: HomeConstants.PAGE_TITLE_HOME,
            icon: 'home',
        };
    }, [pathname]);

    const roundsButton = useMemo(() => {
        return {
            id: 2,
            opened: pathname.startsWith(RoundsConstants.PATH),
            path: RoundsConstants.PATH,
            isLink: true,
            label: t('SmartCargas.SmartCargas'),
        };
    }, [t, pathname]);

    const poolButton = useMemo(() => {
        return {
            id: 7,
            opened: pathname.startsWith(PoolConstants.PATH),
            path: PoolConstants.PATH,
            isLink: true,
            label: t('LoadsPool'),
        };
    }, [t, pathname]);

    const winnersButton = useMemo(() => {
        return {
            id: 3,
            opened: pathname.startsWith(WonOfferConstants.PATH),
            path: WonOfferConstants.PATH,
            isLink: true,
            label: t('SmartCargas.WonOffers.Title'),
        };
    }, [pathname, t]);

    const loadsButton = useMemo(() => {
        return {
            id: 1,
            opened: pathname.startsWith(LoadsConstants.PATH),
            path: LoadsConstants.PATH,
            isLink: false,
            label: t(LoadsConstants.PAGE_TITLE),
            icon: 'shopping-cart',
        };
    }, [pathname, t]);

    const reportsButton = useMemo(() => {
        return {
            id: 6,
            opened: pathname.startsWith(ReportsConstants.PATH),
            path: ReportsConstants.PATH,
            isLink: false,
            label: t(ReportsConstants.PAGE_TITLE),
            icon: 'download',
        };
    }, [pathname, t]);

    const carriersButton = useMemo(() => {
        const userType =
            user?.type === 'third'
                ? t(CarriersConstants.PAGE_TITLE_SINGULAR)
                : t(CarriersConstants.PAGE_TITLE);

        return {
            id: 4,
            opened: pathname.startsWith(CarriersConstants.PATH),
            path: CarriersConstants.PATH,
            isLink: true,
            label: t(userType),
            icon: 'report',
        };
    }, [pathname, user?.type, t]);

    const verifyLanguageDefault = useCallback(() => {
        const languageInLocalStorage = localStorage.getItem(
            'UserLanguageSelected',
        );

        const userLangNavigator = navigator.language;

        if (languageInLocalStorage == null) {
            localStorage.setItem('UserLanguageSelected', userLangNavigator);
            changeLanguage(userLangNavigator);
        } else {
            changeLanguage(languageInLocalStorage);
        }
    }, [changeLanguage]);

    const getFeatureFlags = useCallback(async () => {
        setGerencialFeatureFlag(
            await isFeatureFlagEnabled(featureType.SmartGerencial),
        );
        setSmartWebFeatureFlag(
            await isFeatureFlagEnabled(featureType.SmartWeb),
        );
        setSmartPoolFeatureFlag(
            await isFeatureFlagEnabled(featureType.SmartPool),
        );
    }, [setGerencialFeatureFlag, setSmartWebFeatureFlag]);

    useEffect(() => {
        verifyLanguageDefault();
        getFeatureFlags();
    }, [verifyLanguageDefault, getFeatureFlags]);

    const usersManagementButton = useMemo(() => {
        return {
            id: 5,
            opened: pathname.startsWith(UsersConstants.PATH),
            path: UsersConstants.PATH,
            isLink: true,
            label: t(UsersConstants.PAGE_TITLE),
            icon: 'user',
        };
    }, [pathname, t]);

    const headerOptions = useMemo(() => {
        const options: any = [];

        options.push(homeButton);
        if (
            smartWebFeatureFlag &&
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ])
        ) {
            options.push(roundsButton);
            if (smartPoolFeatureFlag) {
                options.push(poolButton);
            }
            options.push(winnersButton);
        }
        if (
            gerencialFeatureFlag &&
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
            ])
        ) {
            options.push(loadsButton);
        }

        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
            ]) ||
            (smartWebFeatureFlag &&
                isAuthorized([
                    SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                    SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                ]))
        ) {
            options.push(reportsButton);
        }

        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ])
        ) {
            options.push(carriersButton);
        }

        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ])
        ) {
            options.push(usersManagementButton);
        }

        return options;
    }, [
        homeButton,
        smartWebFeatureFlag,
        isAuthorized,
        gerencialFeatureFlag,
        roundsButton,
        winnersButton,
        loadsButton,
        reportsButton,
        carriersButton,
        usersManagementButton,
        poolButton,
    ]);

    function renderEmptyHeader() {
        return (
            <CustomHeader
                className="header-custom-sula"
                productName="SULA"
                empty
                logo={LOGO}
                dataTestId="empty-header"
            />
        );
    }

    const avatarOptions = useMemo(() => {
        return [
            {
                id: 0,
                label: t('Preferências'),
                isLink: false,
                callback: () => navigate(PreferencesConstants.PATH),
            },
            {
                id: 1,
                label: 'Logout',
                isLink: false,
                callback: signOut,
            },
        ] as TAvatarMenuOptions[];
    }, [signOut, t, navigate]);

    const renderHeader = useCallback(() => {
        return (
            <CustomHeader
                logo={LOGO}
                dataTestId="full-header"
                className="header-custom-sula"
                productName="SULA"
                enableAvatar
                enableUserImage={false}
                empty={false}
                username={userName}
                links={headerOptions}
                userProfile={ProfileIcon}
                avatarMenuOptions={avatarOptions}
            />
        );
    }, [avatarOptions, headerOptions, userName]);

    return (
        <NotificationsProvider>
            {!user && renderEmptyHeader()}
            {user && renderHeader()}
        </NotificationsProvider>
    );
}

export default HeaderComponent;
